import { useDisclosure } from "@chakra-ui/hooks";
import AtriumApi, { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import DataTable from "components/data-table";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { Checkbox, InputField, Select } from "components/fields";
import { EmptyState, ErrorState } from "components/states";
import { Form, Formik } from "formik";
import notify from "lib/notify";
import { useEffect, useState } from "react";
import { MdAdd, MdDeleteOutline, MdEdit, MdLocationCity } from "react-icons/md";
import * as Yup from "yup";
import { EMPTY } from "utils/format";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import SettingsSection from "./components/SettingsSection";

const EMPTY_CAMPUS_FORM = {
  name: "",
  campusNumber: "",
  campusLead: "",
  campusLeadId: "",
  isPrimary: false,
  address: "",
  street: "",
  city: "",
  state: "",
  zipCode: "",
};

const campusSchema = Yup.object().shape({
  name: Yup.string().trim().required("Campus name is required"),
});

const CampusManagement = () => {
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [campusForm, setCampusForm] = useState(EMPTY_CAMPUS_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const campusDialog = useDisclosure();
  const confirm = useConfirm();

  const getCampuses = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await AtriumApi.get("/v2/campuses/allCampuses");
      const campusData = response?.data?.data?.campuses?.data;
      setCampuses(Array.isArray(campusData) ? campusData : []);
    } catch (error) {
      setLoadError(error);
      setCampuses([]);
    } finally {
      setLoading(false);
    }
  };

  const getStaffList = async () => {
    try {
      const response = await AtriumApi.get("/v1/get-staff");
      if (response?.data?.data) {
        setStaffList(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load staff:", error);
    }
  };

  useEffect(() => {
    getCampuses();
    getStaffList();
  }, []);

  const handleOpenCampusDialog = (campus = null) => {
    setSelectedCampus(campus);
    setCampusForm(
      campus
        ? {
            name: campus.name || "",
            campusNumber: campus.campusNumber || "",
            campusLead: campus.campusLead || "",
            campusLeadId: campus.campusLeadId || "",
            isPrimary: campus.isPrimary || false,
            address: campus.address || "",
            street: campus.street || "",
            city: campus.city || "",
            state: campus.state || "",
            zipCode: campus.zipCode || "",
          }
        : EMPTY_CAMPUS_FORM
    );
    campusDialog.onOpen();
  };

  const handleSaveCampus = async (values) => {
    setIsSaving(true);
    try {
      const payload = {
        name: values.name,
        campusNumber: values.campusNumber || null,
        campusLead: values.campusLead || null,
        campusLeadId: values.campusLeadId || null,
        isPrimary: values.isPrimary,
        address: values.address || null,
        street: values.street || null,
        city: values.city || null,
        state: values.state || null,
        zipCode: values.zipCode || null,
        status: "active",
      };

      if (selectedCampus) {
        await AtriumApi.patch(`/v2/campuses/${selectedCampus.id}`, payload);
      } else {
        await AtriumApi.post("/v2/campuses", payload);
        track(EVENTS.SETTINGS_CAMPUS_CREATED);
      }
      notify.success("Campus saved");

      campusDialog.onClose();
      getCampuses();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save campus"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCampus = async (campus) => {
    const confirmed = await confirm({
      title: `Delete "${campus.name}"?`,
      message: "Staff access and schedules tied to this campus will no longer apply. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await AtriumApi.delete(`/v2/campuses/${campus.id}`);
      notify.success("Campus deleted");
      getCampuses();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not delete campus"));
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-fg">{row.original.name}</span>
          {row.original.isPrimary && <Badge tone="brand">Primary</Badge>}
        </div>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: ({ row }) => (row.original.isPrimary ? "Main Campus" : "Campus"),
    },
    {
      id: "lead",
      header: "Campus Lead",
      cell: ({ row }) => {
        const campus = row.original;
        return campus.campusLeader
          ? `${campus.campusLeader.firstName} ${campus.campusLeader.lastName}`
          : campus.campusLead || EMPTY;
      },
    },
    {
      id: "location",
      header: "Location",
      cell: ({ row }) =>
        [row.original.city, row.original.state].filter(Boolean).join(", ") || EMPTY,
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { align: "right" },
      cell: ({ row }) => {
        const campus = row.original;
        return (
          <div className="flex items-center justify-end gap-1">
            <IconButton
              icon={<MdEdit />}
              label={`Edit ${campus.name}`}
              size="sm"
              onClick={() => handleOpenCampusDialog(campus)}
            />
            <IconButton
              icon={<MdDeleteOutline />}
              label={campus.isPrimary ? "The primary campus cannot be deleted" : `Delete ${campus.name}`}
              size="sm"
              disabled={campus.isPrimary}
              onClick={() => handleDeleteCampus(campus)}
            />
          </div>
        );
      },
    },
  ];

  const addButton = (
    <Button leftIcon={<MdAdd />} onClick={() => handleOpenCampusDialog()}>
      Add Campus
    </Button>
  );

  return (
    <SettingsSection
      title="Campuses"
      description="Manage your church campuses and locations."
      action={!loadError && (loading || campuses.length > 0) ? addButton : null}
    >
      {loadError ? (
        <ErrorState
          message={getApiErrorMessage(loadError, "Could not load campuses.")}
          onRetry={getCampuses}
        />
      ) : !loading && campuses.length === 0 ? (
        <EmptyState
          icon={MdLocationCity}
          title="No Campuses Yet"
          description="Add your first campus to organize attendance, staff access and scheduling by location."
          action={addButton}
        />
      ) : (
        <DataTable
          variant="plain"
          aria-label="Campuses"
          columns={columns}
          data={campuses}
          loading={loading}
          getRowId={(c) => String(c.id)}
        />
      )}

      <Dialog
        isOpen={campusDialog.isOpen}
        onClose={campusDialog.onClose}
        title={selectedCampus ? "Edit Campus" : "Add Campus"}
        footer={
          <>
            <Button variant="secondary" onClick={campusDialog.onClose}>
              Cancel
            </Button>
            <Button type="submit" form="campus-form" loading={isSaving}>
              {selectedCampus ? "Save Changes" : "Add Campus"}
            </Button>
          </>
        }
      >
        <Formik
          enableReinitialize
          initialValues={campusForm}
          validationSchema={campusSchema}
          onSubmit={handleSaveCampus}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
            const field = (name) => ({
              name,
              value: values[name],
              error: errors[name],
              touched: touched[name],
              onChange: handleChange,
              onBlur: handleBlur,
            });
            return (
              <Form id="campus-form" noValidate className="flex flex-col gap-4">
                <InputField label="Campus Name" required placeholder="e.g. Main Campus" {...field("name")} />
                <Checkbox
                  name="isPrimary"
                  label="Primary Campus"
                  description="Primary campus admins have access to all other campuses."
                  checked={values.isPrimary}
                  onChange={(e) => setFieldValue("isPrimary", e.target.checked)}
                />
                <Select label="Campus Lead" {...field("campusLeadId")}>
                  <option value="">No campus lead</option>
                  {staffList.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.firstName} {staff.lastName}
                    </option>
                  ))}
                </Select>

                <fieldset className="flex flex-col gap-4 border-t border-line pt-4">
                  <legend className="float-left mb-1 w-full text-sm font-semibold text-fg">
                    Campus Address
                  </legend>
                  <InputField label="Address" placeholder="123 Main Street" {...field("address")} />
                  <InputField label="Street" placeholder="Street name" {...field("street")} />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <InputField label="City" placeholder="City" {...field("city")} />
                    <InputField label="State" placeholder="State" {...field("state")} />
                  </div>
                  <InputField label="Zip Code" placeholder="12345" {...field("zipCode")} />
                </fieldset>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </SettingsSection>
  );
};

export default CampusManagement;
